import { apiBaseUrl } from './client.js';

/** Browser calls — use `apiBaseUrl` so production SPA → API host works. */
const api = apiBaseUrl.replace(/\/$/, '');

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  let json: { data?: T; errors?: { message?: string }[] };
  try {
    json = JSON.parse(text) as { data?: T; errors?: { message?: string }[] };
  } catch {
    throw new Error(
      res.ok
        ? 'Invalid JSON from server'
        : `Request failed (${res.status}). Check VITE_API_URL is set to the API host.`,
    );
  }
  if (!res.ok) {
    const msg = json.errors?.[0]?.message ?? res.statusText;
    throw new Error(msg);
  }
  if (json.data === undefined) throw new Error('Invalid response');
  return json.data;
}

export type PublicStoreInfo = {
  storeName: string;
  logoUrl: string | null;
  loyaltyEnabled: boolean;
  loyaltyEarnRate: string;
  idleMessage: string | null;
  displayShowTaxLine?: boolean;
  displayShowDevicePin?: boolean;
  displayShowDeviceCondition?: boolean;
  queueEnabled?: boolean;
  queueAudioAlert?: boolean;
};

export type PublicQueueDisplay = {
  enabled: boolean;
  serving?: Array<{
    id: string;
    displayName: string;
    reason?: string | null;
    deviceLabel?: string | null;
    estimatedWaitMinutes?: number | null;
    status?: string;
    queueNumber?: number;
  }>;
  waiting?: Array<{
    id: string;
    displayName: string;
    reason?: string | null;
    deviceLabel?: string | null;
    estimatedWaitMinutes?: number | null;
    status?: string;
    queueNumber?: number;
  }>;
  waitingMore?: number;
  averageWaitMinutes?: number | null;
  idleMessage?: string | null;
  emptyHeadline?: string;
  showReason?: boolean;
  showDevice?: boolean;
  audioAlert?: boolean;
  updatedAt?: string;
  storeName?: string | null;
  logoUrl?: string | null;
};

export function fetchPublicStoreInfo(storeId: string) {
  return fetch(`${api}/api/v1/public/stores/${storeId}/info`).then((r) =>
    parseJson<PublicStoreInfo>(r),
  );
}

export function fetchPublicQueueDisplay(storeId: string) {
  return fetch(`${api}/api/v1/public/stores/${storeId}/queue-display`).then((r) =>
    parseJson<PublicQueueDisplay>(r),
  );
}

export type LoyaltyLookupResult =
  | { found: false }
  | {
      found: true;
      firstName: string;
      loyaltyPointsBalance: string | number | null;
      storeCreditBalance: string | number | null;
    };

export function lookupPublicCustomer(phone: string, storeId: string) {
  const q = new URLSearchParams({ phone, storeId });
  return fetch(`${api}/api/v1/public/customers/lookup?${q}`).then((r) =>
    parseJson<LoyaltyLookupResult>(r),
  );
}

export type PublicCustomerInfoBody = {
  storeId: string;
  sessionToken: string;
  customerInfo: {
    firstName: string;
    lastName?: string;
    phone: string;
    email?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
};

export function submitPublicCustomerInfo(body: PublicCustomerInfoBody) {
  return fetch(`${api}/api/v1/public/display/customer-info`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => parseJson<{ success: true }>(r));
}

export function submitPublicWaiverSign(
  ticketId: string,
  body: {
    displayToken: string;
    signatureDataUrl: string;
    waiverType: 'pre_repair' | 'post_repair';
    waiverVersion: string;
  },
) {
  return fetch(`${api}/api/v1/public/tickets/${ticketId}/waiver/sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => parseJson<{ success: true }>(r));
}

export function submitPublicForm(body: {
  displayToken: string;
  fieldValues: Record<string, unknown>;
}) {
  return fetch(`${api}/api/v1/public/forms/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => parseJson<{ success: true; submissionId: string }>(r));
}
